import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Flex, Tab, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { NotificationBadge } from 'uiToolkitV1/NotificationBadge';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const TransientProps = ['isActive'];

const getBaseTabStyles = (theme, props) => ({
  height: props.height || (props.size === 'small' ? '40px' : '52px'),
  padding: props.size === 'small' ? 0 : '8px',
  minWidth: props.size === 'small' ? '76px' : '112px',
  borderColor: props.isActive ? `${theme.colors.button.blue.default} !important` : 'transparent !important',
  borderBottom: '2px solid',
  boxShadow: 'none !important',

  transition: 'border-color 0s',

  '&:active': {
    backgroundColor: 'transparent',
  },

  '& p': {
    color: props.isActive ? theme.colors.button.blue.default : theme.colors.text.g2,
  },
  '& svg': {
    color: props.isActive ? theme.colors.button.blue.default : theme.colors.text.g2,
  },
  '&:hover:enabled': {
    cursor: 'pointer',
    '& p': {
      color: props.isActive ? theme.colors.button.blue.default : theme.colors.text.g1,
    },
    '& svg': {
      color: props.isActive ? theme.colors.button.blue.default : theme.colors.text.g1,
    },
  },
});

export const StyledNavItem = styled(Tab, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, size, ...props }) => ({
  ...getBaseTabStyles(theme, { ...props, size }),
}));

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ size }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const NavItem = ({
  size = 'default',
  Icon = null,
  HoverIcon = null,
  ActiveIcon = null,
  isActive = false,
  notificationCount = 0,
  isBetaFeature = false,
  height,
  // notificationAnchorOrigin,
  children,
}) => {
  const theme = useTheme();
  const ref = useRef(null);

  const [hovering, setHovering] = useState(false);
  const onMouseEnterHandler = useCallback(() => {
    setHovering(true);
  }, []);
  const onMouseLeaveHandler = useCallback(() => {
    setHovering(false);
  }, []);

  useEffect(() => {
    if (isActive) {
      if (!ref) return;
      ref.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [isActive]);

  let IconComp;

  if (Icon) {
    IconComp = Icon;
  }

  if (hovering && !!HoverIcon && !isActive) {
    IconComp = HoverIcon;
  }

  if (isActive && !!ActiveIcon) {
    IconComp = ActiveIcon;
  }

  return (
    <StyledNavItem
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
      isActive={isActive}
      size={size}
      height={height}
      ref={ref}
    >
      <StyledBox size={size}>
        <NotificationBadge
          notificationCount={notificationCount}
          transform={size === 'small' ? 'translate(10px, 2px)' : 'translate(16px, 2px)'}
        >
          <Flex alignItems="center">
            <IconComp fill="transparent" boxSize={size === 'small' ? '24px' : '20px'} />
            {isBetaFeature && <BetaTag ml={size === 'small' ? 1 : 2} />}
          </Flex>
        </NotificationBadge>
        <TextBodyMedium bold={isActive} color={theme.colors.text.g2}>
          {children}
        </TextBodyMedium>
      </StyledBox>
    </StyledNavItem>
  );
};

export { NavItem };
